import Link from 'next/link';

import JsonLd from '@/components/seo/JsonLd';
import FadeIn from '@/components/ui/FadeIn';
import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import type { AppLocale } from '@/lib/i18n/locales';
import { BASE_URL } from '@/utils/constants';

const Breadcrumbs = ({ items, locale }: BreadcrumbsProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.href && { item: `${BASE_URL}${item.href}` }),
    })),
  };

  return (
    <div className="sticky top-header z-40 bg-background -mx-container-x px-container-x py-3 mb-6">
      <FadeIn>
        <JsonLd data={schema} />
        <nav aria-label={COMMON_I18N[locale].breadcrumb}>
          <ol className="flex items-center gap-2 text-sm text-text-secondary">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              return (
                <li key={index} className="flex items-center gap-2">
                  {isLast ? (
                    <span
                      className="text-text truncate max-w-[200px] tablet:max-w-xs"
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  ) : (
                    <>
                      <Link
                        href={item.href!}
                        className="hover:text-primary transition-colors"
                      >
                        {item.label}
                      </Link>
                      <span aria-hidden="true">/</span>
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </FadeIn>
    </div>
  );
};

export default Breadcrumbs;

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  locale: AppLocale;
};
